import { clampMonth, currentBucket, bucketMetaFor, isDay2Exiter, strandedPlanOf, waveOf, } from './decisions.js';
import { tsaById } from './economics.js';
/**
 * Every chip this person's card carries, in the order the frozen app pushes them.
 *
 * `enabled` is a parameter rather than the caller's business because the node-display toggle is PART of
 * the condition in the original, and putting it here is what lets a harness compare the toggled-off case
 * — which is a real state a colleague can put every card into, since the display config is a command.
 */
export function stageMarksOf(design, person, enabled) {
    if (!enabled)
        return [];
    const marks = [];
    const isBuildUp = person.isBuildUp === true;
    const showStage = currentBucket(design, person.id) === 'Carve-out';
    if (showStage) {
        // Read through `bucketMetaFor`, whose absent-entry default is 'perimeter' — so a carve member with
        // no stored metadata is NOT a Day 1 transfer, which is the same answer the original's raw read gives.
        if (!isBuildUp && bucketMetaFor(design, person.id)?.joinStage === 'day1') {
            marks.push({
                kind: 'day1Join',
                text: '⇄D1',
                title: 'Joins the carve-out at Day 1 (transfer)',
                tone: 'muted',
            });
        }
        if (isBuildUp && waveOf(person) === 'day2') {
            /*
              The suffix needs the link to RESOLVE, not merely to be set.
      
              A hire pointing at an agreement this scenario no longer has is not a TSA replacement, and saying
              '·TSA' about it would put a claim on the card that the register contradicts. The unlinked
              tooltip is the honest one for that case, and it is the frozen app's own behaviour (`:4251`).
            */
            const tsaId = person.tsaId ?? null;
            const tsa = tsaId ? tsaById(design, tsaId) : undefined;
            const neededBy = person.neededBy ?? null;
            marks.push({
                kind: 'day2BuildUp',
                text: tsa ? '＋D2·TSA' : '＋D2',
                title: tsa
                    ? `Day 2 build-up replacing TSA “${tsa.name}”${neededBy != null ? ` — needed by M${neededBy}` : ''}`
                    : 'Day 2 build-up (post-separation)',
                tone: 'violet',
            });
        }
        if (isDay2Exiter(design, person)) {
            marks.push({
                kind: 'day2Exit',
                text: '✕D2',
                title: 'Exits at Day 2 (rightsizing)',
                tone: 'danger',
            });
        }
    }
    /*
      The RemainCo mirror, and NOT under `showStage` — the same toggle, but its own condition.
  
      Reading this as part of the carve-out block is the mistake that would make the stranded plan
      invisible on exactly the people who carry one, since `strandedEligible` rejects anybody in the
      carve-out. A planned hire is excluded here and again inside `strandedPlanOf`.
    */
    if (!isBuildUp) {
        const plan = strandedPlanOf(design, person);
        if (plan) {
            const month = clampMonth(plan.month);
            marks.push(plan.action === 'rightsize'
                ? {
                    kind: 'strandedRightsize',
                    text: `✕M${month}`,
                    title: `Stranded plan: rightsized from RemainCo at month ${month} after Day 1`,
                    tone: 'danger',
                }
                : {
                    kind: 'strandedRedeploy',
                    text: `↻M${month}`,
                    title: `Stranded plan: capacity redeployed within RemainCo from month ${month}`,
                    tone: 'accent',
                });
        }
    }
    return marks;
}
/**
 * The card's outline, which is one slot rather than a set.
 *
 * Exit first, matching `:4394`. The original also carries a `!exits` term on the stranded branch that
 * can never fire — `isDay2Exiter` requires Carve-out and `strandedEligible` rejects it — so the ORDER is
 * ported and the dead term is not, because reproducing an unreachable guard invites the next reader to
 * work out whether it matters.
 *
 * A group card reads this off its designated head, which is why it takes a person rather than being
 * folded into `stageMarksOf`'s return.
 */
export function stageOutlineOf(design, person, enabled) {
    if (!enabled)
        return null;
    if (currentBucket(design, person.id) === 'Carve-out' && isDay2Exiter(design, person)) {
        return 'day2exit';
    }
    if (person.isBuildUp !== true && strandedPlanOf(design, person)?.action === 'rightsize') {
        return 'strandedexit';
    }
    return null;
}
//# sourceMappingURL=stage-markers.js.map